import { Loader } from 'components';
import { FC, FormEventHandler, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getCustomers, getLocations, postUser } from 'services';
import { Customer, Location, Role, UserProfile } from 'types';
import { mapById } from 'utils';
import { UserCustomers, UserLocations, UserTruckingCompanies } from './user-associations';
import { UserInformation } from './user-information';

import './new-user.scss';

export const NewUser: FC = () => {
  const navigate = useNavigate();

  // user states
  const [user, setUser] = useState<Partial<UserProfile>>({});
  const role = useMemo(() => user.role ?? Role.NONE, [user.role]);
  const customers = useMemo(() => mapById(user.customers ?? []), [user.customers]);
  const locations = useMemo(() => mapById(user.locations ?? []), [user.locations]);

  const [allCustomers, setAllCustomers] = useState<Map<number, Customer>>(new Map());
  const [allLocations, setAllLocations] = useState<Map<number, Location>>(new Map());

  useEffect(() => {
    getLocations().then(mapById).then(setAllLocations);
    getCustomers().then(mapById).then(setAllCustomers);
  }, []);

  // form states/refs
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const formRef = useRef<HTMLFormElement>(null);
  const usernameRef = useRef<HTMLInputElement>(null);
  const emailRef = useRef<HTMLInputElement>(null);
  const roleRef = useRef<HTMLSelectElement>(null);

  const handleReset: FormEventHandler<HTMLFormElement> = useCallback(() => {
    navigate('/settings/users', { replace: true });
  }, [navigate]);

  const handleSubmit: FormEventHandler<HTMLFormElement> = useCallback(
    async e => {
      e.preventDefault();
      setIsSubmitting(true);

      if (!user || !user.username || !user.email || !user.role || !user.defaultLocation) {
        setIsSubmitting(false);
        return;
      }
      
      const response = await postUser({
        username: user.username,
        email: user.email,
        role: user.role,
        defaultLocation: user.defaultLocation,
        customers: user.customers ?? [],
        locations: user.locations ?? [],
        truckingCompanies: user.truckingCompanies ?? []
      });

      setIsSubmitting(false);
      navigate(`/settings/users`, {
        replace: true,
        state: { message: !response.ok ? new Error(`Create User Failed: ${response.statusText}`) : 'Create User Success' }
      });
    },
    [navigate, user]
  );

  return (
    <form className="page" id="new-user" onReset={handleReset} onSubmit={handleSubmit} ref={formRef}>
      <div className="page-header">
        <h1>NEW USER</h1>
        <div className="d-flex g-3 flex-row-reverse">
          <Loader loading={isSubmitting}>
            <button className="btn-primary" type="submit">
              SUBMIT
            </button>
          </Loader>
          <button className="btn-outline" type="reset">
            CANCEL
          </button>
        </div>
      </div>
      <div className="page-content flex-fill">
        <UserInformation
          user={user}
          userLocations={locations}
          allLocations={allLocations}
          setUser={setUser}
          usernameRef={usernameRef}
          emailRef={emailRef}
          roleRef={roleRef}
        />
        <div className="grid-auto">
          <UserCustomers user={user} role={role} customers={customers} allCustomers={allCustomers} setUser={setUser} />
          <UserLocations user={user} role={role} locations={locations} allLocations={allLocations} setUser={setUser} />
          <UserTruckingCompanies user={user} role={role} setUser={setUser} />
        </div>
      </div>
    </form>
  );
};
